/**
 * Iter 250 — Additional widgets for the Commercial Panel:
 *  - TranscriptLiveWidget : streams the agent's currently-active translator session (poll 2.5s)
 *  - HotLeadsWidget       : top 5 prospects hot/warm with score
 *  - RecentNotificationsWidget : last 5 notifications + unread badge
 *  - QuickNotesWidget     : per-user fast scratchpad (add / delete)
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { WidgetShell } from "./widgets";
import {
  Radio, Flame, Bell, StickyNote, Loader2, ArrowRight, X, Plus,
  Mic2, Sparkles, MessageSquare, AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

// ─── Transcript Live widget ────────────────────────────────────────────

export function TranscriptLiveWidget({ api }) {
  const [data, setData] = useState({ active: false, exchanges: [] });
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/commercial-panel/widget-data/transcript-live");
        if (cancelled) return;
        setData(r.data);
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    poll();
    const id = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [api]);

  // Auto-scroll to bottom when new exchanges arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data.exchanges?.length]);

  return (
    <WidgetShell
      title="Transcript en direct"
      icon={Radio}
      action={data.active ? (
        <span className="text-[10px] text-emerald-400 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
        </span>
      ) : (
        <Link to="/admin/aircall/translator" className="text-[10px] text-cyan-400 hover:underline">Ouvrir →</Link>
      )}
      testid="widget-transcript-live"
    >
      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-cyan-400" /></div>
      ) : !data.active ? (
        <div className="text-center py-6">
          <Mic2 className="w-8 h-8 text-slate-700 mx-auto mb-2" />
          <div className="text-xs text-slate-400">Aucune session active</div>
          <Link to="/admin/aircall/translator" className="text-[10px] text-cyan-400 hover:underline mt-1 inline-block">
            Démarrer une session →
          </Link>
        </div>
      ) : (
        <div className="space-y-2 h-full flex flex-col">
          <div className="text-[10px] text-slate-500 flex items-center justify-between">
            <span>{data.exchanges.length} échange{data.exchanges.length > 1 ? "s" : ""}</span>
            <span>{data.session?.contact_name || "Contact"}</span>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-1.5 pr-1 max-h-[260px]">
            {data.exchanges.length === 0 ? (
              <div className="text-center text-[11px] text-slate-500 py-4">En attente du premier échange…</div>
            ) : (
              data.exchanges.map((ex) => {
                const isAgent = ex.direction === "agent_to_contact";
                return (
                  <div key={ex.exchange_id} className={`text-[11px] rounded p-1.5 border ${isAgent ? "bg-blue-500/5 border-blue-500/20" : "bg-emerald-500/5 border-emerald-500/20"}`}>
                    <div className="text-[9px] uppercase text-slate-500 mb-0.5">
                      {isAgent ? "Vous" : "Contact"} · {(ex.lang_source || "").toUpperCase()} → {(ex.lang_target || "").toUpperCase()}
                    </div>
                    <div className="text-white">{ex.transcript}</div>
                    <div className="text-slate-300 italic mt-0.5">→ {ex.translation}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </WidgetShell>
  );
}

// ─── Hot Leads widget ──────────────────────────────────────────────────

export function HotLeadsWidget({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/hot-leads").then((r) => {
      if (!cancelled) setItems(r.data.items || []);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  const tierColors = {
    hot: "text-rose-400 bg-rose-500/10 border-rose-500/30",
    warm: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    cold: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  };

  return (
    <WidgetShell title="Leads chauds" icon={Flame} testid="widget-hot-leads">
      {loading ? <Spinner /> :
        items.length === 0 ? (
          <div className="text-center py-4">
            <div className="text-xs text-slate-400">Aucun lead chaud actuellement</div>
            <div className="text-[10px] text-slate-600 mt-1">Scorez vos prospects pour qu'ils apparaissent ici</div>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {items.map((p) => (
              <li key={p.prospect_id}>
                <Link to={`/admin/prospects/${p.prospect_id}`} className="block p-2 rounded hover:bg-white/[0.04] border border-transparent hover:border-white/10">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-white font-medium truncate">
                        {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {p.company || p.email || p.phone || "—"}
                      </div>
                    </div>
                    <div className={`shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-bold ${tierColors[p.tier] || tierColors.cold}`}>
                      {p.score ?? "—"}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )
      }
    </WidgetShell>
  );
}

// ─── Recent Notifications widget ──────────────────────────────────────

export function RecentNotificationsWidget({ api }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/recent-notifications").then((r) => {
      if (!cancelled) setData(r.data);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);
  return (
    <WidgetShell
      title="Notifications récentes"
      icon={Bell}
      action={data.unread > 0 ? (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white font-bold">
          {data.unread}
        </span>
      ) : null}
      testid="widget-recent-notifications"
    >
      {loading ? <Spinner /> :
        data.items.length === 0 ? (
          <div className="text-center py-4 text-xs text-slate-400">Pas de notification</div>
        ) : (
          <ul className="space-y-1.5">
            {data.items.map((n, i) => (
              <li key={n.notification_id || i} className={`text-xs p-1.5 rounded ${n.read ? "bg-white/[0.02]" : "bg-cyan-500/5 border border-cyan-500/20"}`}>
                <div className="flex items-start gap-2">
                  {n.read ? (
                    <CheckCircle2 className="w-3 h-3 text-slate-600 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-3 h-3 text-cyan-400 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate font-medium">{n.title || n.category || "—"}</div>
                    {n.message && <div className="text-[10px] text-slate-400 truncate">{n.message}</div>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )
      }
    </WidgetShell>
  );
}

// ─── Quick Notes widget ───────────────────────────────────────────────

export function QuickNotesWidget({ api }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");

  const reload = async () => {
    try {
      const r = await api.get("/commercial-panel/widget-data/quick-notes");
      setNotes(r.data.items || []);
    } catch (e) { /* */ }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/commercial-panel/widget-data/quick-notes").then((r) => {
      if (!cancelled) setNotes(r.data.items || []);
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [api]);

  const addNote = async () => {
    const txt = draft.trim();
    if (!txt) return;
    try {
      await api.post("/commercial-panel/widget-data/quick-notes", { text: txt });
      setDraft("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeNote = async (note_id) => {
    try {
      await api.delete(`/commercial-panel/widget-data/quick-notes/${note_id}`);
      setNotes((p) => p.filter((n) => n.note_id !== note_id));
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  return (
    <WidgetShell title="Notes rapides" icon={StickyNote} testid="widget-quick-notes">
      <div className="space-y-2 h-full flex flex-col">
        <div className="flex gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && addNote()}
            placeholder="Tapez une note + Entrée…"
            className="flex-1 bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-xs text-white"
            data-testid="quick-notes-input"
          />
          <Button onClick={addNote} size="sm" disabled={!draft.trim()} data-testid="quick-notes-add">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        {loading ? <Spinner /> :
          notes.length === 0 ? (
            <div className="text-center text-[11px] text-slate-500 py-4 flex-1">
              Tapez vos idées commerciales ici
            </div>
          ) : (
            <ul className="space-y-1 overflow-y-auto flex-1 pr-1 max-h-[200px]">
              {notes.map((n) => (
                <li key={n.note_id} className="group bg-amber-500/5 border border-amber-500/20 rounded p-1.5 text-xs text-amber-100 relative">
                  <div className="pr-5">{n.text}</div>
                  <button
                    onClick={() => removeNote(n.note_id)}
                    className="absolute top-1 right-1 text-amber-500/40 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`quick-notes-delete-${n.note_id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="text-[9px] text-amber-500/50 mt-0.5">{formatTime(n.created_at)}</div>
                </li>
              ))}
            </ul>
          )
        }
      </div>
    </WidgetShell>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function Spinner() {
  return <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-cyan-400" /></div>;
}

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

// Suppress unused-warning helpers
export const _unused = { Sparkles, MessageSquare };
